import { create } from "zustand";

export interface CateringItem {
  cateringId: string;
  cateringNumber: string;
  iceClaimNumber: string | null;
  status: string;
  basicInfo: {
    deceasedFullName: string;
    funeralDate: string;
    serviceType: string;
    claimantFullName: string;
    claimantContactNumber: string;
    claimantEmail: string;
    supplementaryName: string;
    cashBenefitValue: string;
    serviceBenefitValue: string;
    isUnderWaitingPeriod: boolean;
  };

  cateringSetup: {
    cateringRequired: boolean;
    guestCount: number;
    servingStations: number;
    menuCategoryId: string;
    menuCategoryName: string;
    servingTime: string;
    servingStyle: string;
    setupTime: string;
    breakdownTime: string;
    customRequests: string | null;
  };
  menu: {
    menuCategoryDisplayName: string;
    items: {
      id?: string;
      templateItemId?: string;
      itemName: string;
      quantity: number;
      unit: string;
      notes: string | null;
    }[];
  };
  logistics: {
    setupLocation: string;
    indoorOutdoor: string;
    tablesRequired: number;
    urnsRequired: number;
    chafingDishesRequired: number;
    tentsRequired: boolean;
    accessNotes: string;
    inventoryStatus?: string;
    supplierAssigned?: string;
    deliveryTime?: string;
  };
  staffing: {
    serversRequired: string;
    supervisorsRequired: string;
    driversRequired: string;
    assignedStaff?: string[];
    staffArrivalTime: string;
  };
  financials?: {
    costPerHead: number;
    estimatedCost?: number;
    actualCost: number;
    budgetVariance?: number;
    billingStatus: string;
    internalNotes: string;
    clientNotes: string;
  };
  missingFieldsFlag: boolean;
  missingFields: string[] | null;
}

interface CateringState {
  catering: CateringItem[];
  totalCount: number;
  statusCounts: Record<string, number>;
  selectedCatering: CateringItem | null;

  setCatering: (data: any) => void;
  setSelectedCatering: (item: CateringItem) => void;
  setCateringBasicInfo: (data: Partial<CateringItem["basicInfo"]>) => void;
  setCateringSetup: (data: Partial<CateringItem["cateringSetup"]>) => void;
  setCateringMenu: (data: Partial<CateringItem["menu"]>) => void;
  addCateringMenuItem: () => void;
  updateCateringMenuItem: (
    index: number,
    data: Partial<CateringItem["menu"]["items"][number]>,
  ) => void;
  removeCateringMenuItem: (index: number) => void;
  setCateringLogistics: (data: Partial<CateringItem["logistics"]>) => void;
  setCateringStaffing: (data: Partial<CateringItem["staffing"]>) => void;
  clearSelectedCatering: () => void;
}

const defaultBasicInfo: CateringItem["basicInfo"] = {
  deceasedFullName: "",
  funeralDate: "",
  serviceType: "",
  claimantFullName: "",
  claimantContactNumber: "",
  claimantEmail: "",
  supplementaryName: "",
  cashBenefitValue: "",
  serviceBenefitValue: "",
  isUnderWaitingPeriod: false,
};

const defaultCateringSetup: CateringItem["cateringSetup"] = {
  cateringRequired: true,
  guestCount: 0,
  servingStations: 2,
  menuCategoryId: "",
  menuCategoryName: "",
  servingTime: "",
  servingStyle: "Buffet",
  setupTime: "",
  breakdownTime:"",
  customRequests: null,
};

const defaultMenuItem: CateringItem["menu"]["items"][number] = {
  id: "",
  templateItemId: "",
  itemName: "",
  quantity: 0,
  unit: "",
  notes: null,
};

const defaultCateringMenu: CateringItem["menu"] = {
  menuCategoryDisplayName: "Menu 1",
  items: [],
};

const defaultCateringLogistics: CateringItem["logistics"] = {
  setupLocation: "Home",
  indoorOutdoor: "Indoor",
  tablesRequired: 0,
  urnsRequired: 0,
  chafingDishesRequired: 0,
  tentsRequired: false,
  accessNotes: "",
  inventoryStatus: "",
  supplierAssigned: "",
  deliveryTime: "",
};

const defaultCateringStaffing: CateringItem["staffing"] = {
  serversRequired: "",
  supervisorsRequired: "",
  driversRequired: "",
  assignedStaff: [],
  staffArrivalTime: "",
};

const defaultCateringFinancials: NonNullable<CateringItem["financials"]> = {
  costPerHead: 0,
  estimatedCost: 0,
  actualCost: 0,
  budgetVariance: 0,
  billingStatus: "",
  internalNotes: "",
  clientNotes: "",
};

export const useCateringStore = create<CateringState>((set) => ({
  catering: [],
  totalCount: 0,
  statusCounts: {},
  selectedCatering: null,

  setCatering: (data) =>
    set({
      catering: data?.catering || [],
      totalCount: data?.totalCount || 0,
      statusCounts: data?.statusCounts || {},
    }),

  setSelectedCatering: (item) =>
    set({
      selectedCatering: {
        ...item,
        basicInfo: {
          ...defaultBasicInfo,
          ...item.basicInfo,
        },
        cateringSetup: {
          ...defaultCateringSetup,
          ...item.cateringSetup,
          servingStations:
            item.cateringSetup?.servingStations ??
            (item.cateringSetup as any)?.numberOfServingStations ??
            2,
          customRequests:
            item.cateringSetup?.customRequests ??
            (item.cateringSetup as any)?.notes ??
            null,
        },
        menu: {
          ...defaultCateringMenu,
          ...item.menu,
          items: item.menu?.items?.length
            ? item.menu.items.map((menuItem) => ({
                ...defaultMenuItem,
                ...menuItem,
                id: menuItem.id || (menuItem as any).menuItemId || "",
                templateItemId:
                  menuItem.templateItemId ||
                  ((menuItem as any).isFromTemplate ? menuItem.id : "") ||
                  "",
                notes:
                  menuItem.notes ??
                  (menuItem as any).preparationNotes ??
                  null,
              }))
            : [],
        },
        logistics: {
          ...defaultCateringLogistics,
          ...item.logistics,
          accessNotes:
            item.logistics?.accessNotes ??
            (item.logistics as any)?.equipmentRequired ??
            "",
        },
        staffing: {
          ...defaultCateringStaffing,
          ...item.staffing,
        },
        financials: {
          ...defaultCateringFinancials,
          ...item.financials,
        },
      },
    }),

  setCateringBasicInfo: (data) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            basicInfo: {
              ...defaultBasicInfo,
              ...state.selectedCatering.basicInfo,
              ...data,
            },
          }
        : null,
    })),

  setCateringSetup: (data) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            cateringSetup: {
              ...defaultCateringSetup,
              ...state.selectedCatering.cateringSetup,
              ...data,
            },
          }
        : null,
    })),

  setCateringMenu: (data) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            menu: {
              ...defaultCateringMenu,
              ...state.selectedCatering.menu,
              ...data,
            },
          }
        : null,
    })),

  addCateringMenuItem: () =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            menu: {
              ...defaultCateringMenu,
              ...state.selectedCatering.menu,
              items: [
                ...(state.selectedCatering.menu?.items || []),
                { ...defaultMenuItem },
              ],
            },
          }
        : null,
    })),

  updateCateringMenuItem: (index, data) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            menu: {
              ...defaultCateringMenu,
              ...state.selectedCatering.menu,
              items: (state.selectedCatering.menu?.items || []).map(
                (item, itemIndex) =>
                  itemIndex === index
                    ? { ...defaultMenuItem, ...item, ...data }
                    : item,
              ),
            },
          }
        : null,
    })),

  removeCateringMenuItem: (index) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            menu: {
              ...defaultCateringMenu,
              ...state.selectedCatering.menu,
              items: (state.selectedCatering.menu?.items || []).filter(
                (_, itemIndex) => itemIndex !== index,
              ),
            },
          }
        : null,
    })),

  setCateringLogistics: (data) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            logistics: {
              ...defaultCateringLogistics,
              ...state.selectedCatering.logistics,
              ...data,
            },
          }
        : null,
    })),

  setCateringStaffing: (data) =>
    set((state) => ({
      selectedCatering: state.selectedCatering
        ? {
            ...state.selectedCatering,
            staffing: {
              ...defaultCateringStaffing,
              ...state.selectedCatering.staffing,
              ...data,
            },
          }
        : null,
    })),

  clearSelectedCatering: () =>
    set({
      selectedCatering: null,
    }),
}));
